import React from 'react';

import PropTypes from 'prop-types';
import Heading from '@tds/core-heading';

import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import {
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  SummarySectionHeading
} from './css';

const Summary = ({ orderData }) => {
  const {
    contractTerm,
    typeOfInstallation: typeOfInstallationValue,
    businessConnectEdition,
    starterSeat: starterSeatValue,
    officeSeats: officeSeatsValue,
    additionalSpareDevices,
    devices,
    features: featuresList,
    howCustomerWantToPayForWhiteGlove,
    numberOfSeatsThatRequireWhiteGlove,
    doesTheCustomerRequireWhiteGlove,
    isThisAnExistingGSuiteCustomer,
    optOutFromGSuiteBusinessLicenses,
    gSuiteComplimentarySeats,
    additionalGSuiteBusinessLicences,
    gSuiteInstallAndSetup,
    gSuiteEmailMigrationServices
  } = orderData;
  const localizedStrings = useLocalizedStringsContext();
  const {
    ORDERS: { ORDER_SUMMARY },
    contentfulValues: { yes, no, valueEasyPayment, valuePaymentUpfront },
    orderTracking: {
      summaryLabels: {
        term,
        typeOfInstallation,
        edition,
        starterSeat,
        officeSeat,
        primaryDevices,
        additionalDevices,
        features
      },
      labelDeviceName,
      labelFeatureName,
      labelQuantity,
      wgUpfrontSocForSite,
      wgUpfrontSocForUser,
      wgEasypaymentSocForUser,
      gsuiteTotalLicences,
      gsuiteTotalLicencesIncluded,
      gsuiteInstallAndSetupIncluded,
      gsuiteEmailMigration
    }
  } = localizedStrings;

  /**
   * Add WG to the features list
   */
  if (doesTheCustomerRequireWhiteGlove === yes) {
    if (howCustomerWantToPayForWhiteGlove === valueEasyPayment)
      featuresList.push({
        description: wgEasypaymentSocForUser,
        quantity: numberOfSeatsThatRequireWhiteGlove
      });
    if (howCustomerWantToPayForWhiteGlove === valuePaymentUpfront) {
      featuresList.push({
        description: wgUpfrontSocForSite,
        quantity: 1
      });
      featuresList.push({
        description: wgUpfrontSocForUser,
        quantity: numberOfSeatsThatRequireWhiteGlove
      });
    }
  }

  /**
   * Add GSuite to the features list
   */
  if (
    isThisAnExistingGSuiteCustomer === no &&
    !optOutFromGSuiteBusinessLicenses
  ) {
    featuresList.push({
      description: gsuiteTotalLicencesIncluded,
      quantity: gSuiteComplimentarySeats
    });
    if (additionalGSuiteBusinessLicences)
      featuresList.push({
        description: gsuiteTotalLicences,
        quantity: additionalGSuiteBusinessLicences
      });
    if (gSuiteInstallAndSetup === yes)
      featuresList.push({
        description: gsuiteInstallAndSetupIncluded,
        quantity: 1
      });
    if (gSuiteEmailMigrationServices)
      featuresList.push({
        description: gsuiteEmailMigration,
        quantity: gSuiteEmailMigrationServices
      });
  }

  return (
    <>
      {orderData && (
        <>
          <Heading level="h2" tag="h4">
            {ORDER_SUMMARY}
          </Heading>
          <div style={{ marginTop: '2rem' }}>
            <SummaryRow>
              <SummaryLabel>{term}</SummaryLabel>
              <SummaryValue>{contractTerm}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{typeOfInstallation}</SummaryLabel>
              <SummaryValue>{typeOfInstallationValue}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{edition}</SummaryLabel>
              <SummaryValue>{businessConnectEdition}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{starterSeat}</SummaryLabel>
              <SummaryValue>{starterSeatValue}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{officeSeat}</SummaryLabel>
              <SummaryValue>{officeSeatsValue}</SummaryValue>
            </SummaryRow>
          </div>
          {devices && devices.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <SummarySectionHeading>{primaryDevices}</SummarySectionHeading>
              <table className="table table-bordered">
                <thead style={{ background: '#f7f7f8' }}>
                  <tr>
                    <th width="20%" scope="col">
                      #
                    </th>
                    <th width="60%" scope="col">
                      {labelDeviceName}
                    </th>
                    <th width="20%" scope="col">
                      {labelQuantity}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device, index) => (
                    <tr>
                      <th scope="row">{index + 1}</th>
                      <td>{device.name}</td>
                      <td>{device.quantity}</td>
                    </tr>
                  ))}
                  {}
                </tbody>
              </table>
            </div>
          )}
          {additionalSpareDevices && additionalSpareDevices.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <SummarySectionHeading>{additionalDevices}</SummarySectionHeading>
              <table className="table table-bordered">
                <thead style={{ background: '#f7f7f8' }}>
                  <tr>
                    <th width="20%" scope="col">
                      #
                    </th>
                    <th width="60%" scope="col">
                      {labelDeviceName}
                    </th>
                    <th width="20%" scope="col">
                      {labelQuantity}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {additionalSpareDevices.map((device, index) => (
                    <tr>
                      <th scope="row">{index + 1}</th>
                      <td>{device.name}</td>
                      <td>{device.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {featuresList && featuresList.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <SummarySectionHeading>{features}</SummarySectionHeading>
              <table className="table table-bordered">
                <thead style={{ background: '#f7f7f8' }}>
                  <tr>
                    <th width="20%" scope="col">
                      #
                    </th>
                    <th width="60%" scope="col">
                      {labelFeatureName}
                    </th>
                    <th width="20%" scope="col">
                      {labelQuantity}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {featuresList.map((device, index) => (
                    <tr>
                      <th scope="row">{index + 1}</th>
                      <td>{device.description}</td>
                      <td>{device.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
};

Summary.propTypes = {
  orderData: PropTypes.objectOf(PropTypes.string).isRequired
};

export default Summary;
